import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  Drawer,
  Link,
  List,
  ListItemButton,
  Stack,
  styled,
  Typography,
} from '@mui/material'
import { FC, useCallback, useMemo, useState } from 'react'
import { IconButton } from 'utoniq-ui'

import {
  CommentBoxFragment,
  CommentBoxWithoutRepliesFragment,
  ReplyBoxFragment,
} from '@/clients/utoniq-core/schema'
import { ThumbnailFullScreenModal } from '@/components/organisms'
import { useMessage, useRouter, useTranslation } from '@/core'
import { usePrivateAction } from '@/lib/privateAction'
import { LinkifyBox } from '@/lib/reactLinkify'

export type ReplyBoxProps = {
  index?: number
  type?: 'missions' | 'threads' | 'items'
  typeId?: string
  userId: string
  divider?: boolean
  showReaction?: boolean
  hiddenReply?: boolean
  comment:
    | CommentBoxFragment
    | ReplyBoxFragment
    | CommentBoxWithoutRepliesFragment
  onDeleteComment: (commentId: string) => Promise<void | boolean>
  onReportUser: (
    id: string,
    commenterId: string,
    commenterAccountId: string
  ) => Promise<void>
  onBlockUser: (id: string) => Promise<void | boolean>
  onLikeOrUnlikeComment?: (input: {
    isLike: boolean
    commentId: string
    id?: string
    likeCount: number
  }) => Promise<void>
  onReplyToReply: (params: {
    accountId: string
    displayName: string
    commentId?: string
  }) => void
  onClickReply: () => void
  rootCollectionId?: string
  unreadIndex?: number
  /**
   * @description
   * いいね機能を無効化しているか否か
   * 無効化している場合はいいね関連機能を非表示にする
   */
  disableLikes?: boolean
}

export const ReplyBox: FC<ReplyBoxProps> = ({
  index,
  type,
  typeId,
  userId,
  showReaction,
  hiddenReply,
  divider,
  comment,
  onDeleteComment,
  onReportUser,
  onBlockUser,
  onLikeOrUnlikeComment,
  onReplyToReply,
  onClickReply,
  rootCollectionId,
  unreadIndex,
  disableLikes = false,
}) => {
  const { t } = useTranslation('postDetail')
  const { showMessage } = useMessage()
  const { handleLoginModal } = usePrivateAction()
  const { router, routerPages, generatePath } = useRouter()
  const [open, setOpen] = useState<boolean>(false)
  const [openFullScreen, setOpenFullScreen] = useState<boolean>(false)
  const userComment = useMemo(
    () => (isComment(comment) ? comment.commenter : comment.responder),
    [comment]
  )

  const showNew = useMemo(
    () => !isNaN(Number(unreadIndex)) && unreadIndex === index,
    [index, unreadIndex]
  )

  const toggleDrawer = useCallback(
    (open: boolean) => () => {
      setOpen(open)
    },
    []
  )

  const handleDeleteReply = useCallback(async () => {
    await onDeleteComment(comment.id).then(
      hiddenMessage =>
        !hiddenMessage && showMessage(t('deleteReply'), 'success')
    )
    setOpen(false)
  }, [onDeleteComment, showMessage, t, comment])

  const handleReportUser = useCallback(async () => {
    await onReportUser(
      comment.id,
      userComment?.id ?? '',
      userComment?.formattedAccountId ?? ''
    )
    setOpen(false)
  }, [
    comment.id,
    onReportUser,
    userComment?.formattedAccountId,
    userComment?.id,
  ])

  const handleBlockUser = useCallback(async () => {
    await onBlockUser(userComment?.id ?? '').then(
      hiddenMessage =>
        !hiddenMessage && showMessage(t('blockUserSuccess'), 'success')
    )
    setOpen(false)
  }, [onBlockUser, showMessage, t, userComment])

  const imageUrl = isComment(comment) ? comment.resources?.[0]?.uri : null

  // リプライを持つ親コメントかどうか
  const isReplyParent =
    !hiddenReply &&
    isCommentWithReplies(comment) &&
    !!comment?.replies?.edges?.length

  if (!userComment) return null

  return (
    <>
      {divider && showNew && (
        <DividerNewStyled color="like.tap" textAlign="right">
          <Typography variant="overline" color="like.tap">
            NEW
          </Typography>
        </DividerNewStyled>
      )}
      <Stack
        flex={1}
        direction="row"
        spacing={2}
        sx={{
          pb: 2,
          position: 'relative',
        }}
      >
        {/* リプライのあるコメントには、コメント詳細へのリンクがつく */}
        {isReplyParent && (
          <ButtonBase
            sx={{
              position: 'absolute',
              zIndex: 0,
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
            onClick={() => {
              if (type && typeId) {
                router.push(
                  generatePath(routerPages.ReplyDetail, {
                    type,
                    id: typeId,
                    commentId: comment.id,
                  })
                )
              }
            }}
          />
        )}
        <Box>
          <Link
            onClick={handleLoginModal()}
            sx={{ position: 'relative', zIndex: 2 }}
            href={generatePath(routerPages.UserProfile, {
              userId: userComment?.id ?? '#',
            })}
          >
            <Avatar
              src={userComment?.avatar?.smallUri ?? ''}
              sx={{ width: 28, height: 28 }}
            />
          </Link>
        </Box>
        <Stack flex={1} sx={{ zInde: 2 }}>
          <Stack direction="row" justifyContent="space-between">
            <Stack flex={1} direction="row" alignItems="center">
              <Link
                onClick={handleLoginModal()}
                href={generatePath(routerPages.UserProfile, {
                  userId: userComment?.id ?? '#',
                })}
                sx={{ textDecoration: 'none', zIndex: 2 }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  {userComment?.displayName}
                </Typography>
              </Link>
              <Typography variant="caption" color="text.hint" sx={{ pl: 2 }}>
                {comment.formattedCreatedAt}
              </Typography>
            </Stack>
            <IconButton.More sx={{ zIndex: 2 }} onClick={toggleDrawer(true)} />
          </Stack>
          {isComment(comment) && imageUrl && (
            <ButtonBase
              onClick={() => setOpenFullScreen(true)}
              sx={{ zIndex: 2 }}
            >
              <Avatar
                variant="square"
                src={imageUrl}
                sx={{ width: '100%', height: 255, my: 1, borderRadius: 1 }}
              />
            </ButtonBase>
          )}
          <LinkifyBox>
            <Typography
              variant="body2"
              color="grey.900"
              sx={{ pt: 1, wordBreak: 'break-all' }}
            >
              {comment.content}
            </Typography>
          </LinkifyBox>
          {showReaction && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flex={1}
            >
              <Box />
              {onLikeOrUnlikeComment && (
                <Stack direction="row" spacing={3} sx={{ py: 2 }}>
                  {!disableLikes && (
                    <Stack direction="row" alignItems="center">
                      <IconButton.Like
                        size="small"
                        liked={comment.isLike}
                        sx={{ zIndex: 2 }}
                        onClick={handleLoginModal(
                          () =>
                            onLikeOrUnlikeComment &&
                            onLikeOrUnlikeComment({
                              commentId: comment.id,
                              id: rootCollectionId,
                              isLike: comment.isLike,
                              likeCount: comment.likeCount || 0,
                            })
                        )}
                      />
                      <Typography variant="caption">
                        {comment.likeCount || 0}
                      </Typography>
                    </Stack>
                  )}
                  {isComment(comment) && !hiddenReply && (
                    <Stack direction="row" alignItems="center">
                      <IconButton.Reply
                        size="small"
                        sx={{ zIndex: 2 }}
                        onClick={() => {
                          onClickReply()
                          if (onReplyToReply) {
                            onReplyToReply({
                              accountId: userComment?.accountId || '',
                              displayName: userComment?.displayName || '',
                              commentId: comment.id || '',
                            })
                            return onClickReply()
                          }
                          if (type && typeId) {
                            return router.push(
                              generatePath(routerPages.ReplyDetail, {
                                type,
                                id: typeId,
                                commentId: comment.id,
                              })
                            )
                          }
                        }}
                      />
                      <Typography variant="caption">
                        {comment.replyCount}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
      <ThumbnailFullScreenModal
        open={openFullScreen}
        onClose={() => setOpenFullScreen(false)}
        resource={{ uri: imageUrl || '' }}
      />
      {divider && unreadIndex != (index || 1) - 1 && <Divider />}
      <Stack direction="column-reverse">
        {isCommentWithReplies(comment) &&
          comment?.replies?.edges?.map(({ node }) => (
            <Box
              key={node.id}
              sx={{
                pt: 5,
                pl: '36px',
                bgcolor: undefined,
              }}
            >
              <ReplyBox
                userId={userId}
                comment={node}
                divider
                showReaction
                hiddenReply
                onReportUser={onReportUser}
                onBlockUser={onBlockUser}
                onDeleteComment={onDeleteComment}
                rootCollectionId={rootCollectionId}
                onLikeOrUnlikeComment={onLikeOrUnlikeComment}
                onReplyToReply={onReplyToReply}
                onClickReply={onClickReply}
              />
            </Box>
          ))}
      </Stack>
      <Drawer open={open} onClose={toggleDrawer(false)}>
        <Box sx={{ p: 5 }}>
          <Typography variant="h6" color="grey.600" align="center">
            {userComment?.displayName ?? ''}
          </Typography>
        </Box>
        <Divider />
        <List>
          {userComment?.id === userId ? (
            <>
              <ListItemButton
                component={Box}
                sx={{ height: 52 }}
                divider
                onClick={handleLoginModal(handleDeleteReply)}
              >
                <Typography variant="h6">{t('delete')}</Typography>
              </ListItemButton>
            </>
          ) : (
            <>
              <ListItemButton
                component={Box}
                sx={{ height: 52 }}
                onClick={handleLoginModal(handleReportUser)}
              >
                <Typography variant="h6">{t('report')}</Typography>
              </ListItemButton>
              <ListItemButton
                component={Box}
                sx={{ height: 52 }}
                divider
                onClick={handleLoginModal(handleBlockUser)}
              >
                <Typography variant="h6">{t('block')}</Typography>
              </ListItemButton>
            </>
          )}
          <ListItemButton
            component={Box}
            sx={{ height: 52 }}
            alignItems="center"
            onClick={toggleDrawer(false)}
          >
            <Typography variant="h6" align="center" sx={{ width: '100%' }}>
              {t('cancel')}
            </Typography>
          </ListItemButton>
        </List>
      </Drawer>
    </>
  )
}

const DividerNewStyled = styled(Divider)(({ theme }) => ({
  '&::before': {
    width: '100%',
    borderColor: theme.palette.like['tap'],
  },
  '&::after': {
    width: 0,
  },
}))

function isComment(
  commentOrReply:
    | CommentBoxFragment
    | ReplyBoxFragment
    | CommentBoxWithoutRepliesFragment
): commentOrReply is CommentBoxFragment | CommentBoxWithoutRepliesFragment {
  return 'commenter' in commentOrReply
}

function isCommentWithReplies(
  commentOrReply:
    | CommentBoxFragment
    | ReplyBoxFragment
    | CommentBoxWithoutRepliesFragment
): commentOrReply is CommentBoxFragment {
  return 'commenter' in commentOrReply
}
